// Connect forms shared by Integrations and the onboarding wizard.
import { useState } from "react";
import { api, ApiError, type MysqlCredentials, type PostgresCredentials } from "../../api";
import { useAsync } from "../../useAsync";
import { deployGtmTag, GTM_DEPLOY_CONFIRM } from "../../lib/gtmDeploy";

export const ONBOARDING_FLAG = "fk_onboarding";

export function startOnboarding(): void {
  localStorage.setItem(ONBOARDING_FLAG, "1");
}

export function finishOnboarding(): void {
  localStorage.removeItem(ONBOARDING_FLAG);
}

export function onboardingInProgress(): boolean {
  return localStorage.getItem(ONBOARDING_FLAG) === "1";
}

export { WebsiteDetector } from "./WebsiteDetector";

export function MysqlCredentialsForm({ slug, onDone }: { slug: string; onDone: () => void }) {
  const [form, setForm] = useState({ host: "", port: "3306", user: "", password: "", database: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });
  const ready = form.host.trim() && form.user.trim() && form.password && form.database.trim();

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const credentials: MysqlCredentials = {
        host: form.host.trim(),
        port: Number(form.port) || 3306,
        user: form.user.trim(),
        password: form.password,
        database: form.database.trim(),
      };
      await api.connectMysql(slug, { credentials, status: "active" });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 560 }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) 90px", gap: 8 }}>
        <input className="input" placeholder="Host (db-replica.example.com)" aria-label="Host"
               value={form.host} onChange={set("host")} />
        <input className="input" placeholder="3306" aria-label="Port" inputMode="numeric"
               value={form.port} onChange={set("port")} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <input className="input" placeholder="Read-only user" aria-label="User"
               value={form.user} onChange={set("user")} autoComplete="off" />
        <input className="input" type="password" placeholder="Password" aria-label="Password"
               value={form.password} onChange={set("password")} autoComplete="new-password" />
      </div>
      <input className="input" placeholder="Database name" aria-label="Database"
             value={form.database} onChange={set("database")} />
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button type="button" className="btn-solid" disabled={!ready || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save credentials"}
        </button>
        <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
          Encrypted at rest. Use a read-only user, ideally on a replica.
        </span>
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

export function PostgresCredentialsForm({ slug, onDone }: { slug: string; onDone: () => void }) {
  const [form, setForm] = useState({ host: "", port: "5432", user: "", password: "", database: "", schema: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });
  const ready = form.host.trim() && form.user.trim() && form.password && form.database.trim();

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const credentials: PostgresCredentials = {
        host: form.host.trim(),
        port: Number(form.port) || 5432,
        user: form.user.trim(),
        password: form.password,
        database: form.database.trim(),
        ...(form.schema.trim() ? { schema: form.schema.trim() } : {}),
      };
      await api.connectPostgres(slug, { credentials, status: "active" });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 560 }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) 90px", gap: 8 }}>
        <input className="input" placeholder="Host (db-replica.example.com)" aria-label="Host"
               value={form.host} onChange={set("host")} />
        <input className="input" placeholder="5432" aria-label="Port" inputMode="numeric"
               value={form.port} onChange={set("port")} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <input className="input" placeholder="Read-only role" aria-label="User"
               value={form.user} onChange={set("user")} autoComplete="off" />
        <input className="input" type="password" placeholder="Password" aria-label="Password"
               value={form.password} onChange={set("password")} autoComplete="new-password" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) 1fr", gap: 8 }}>
        <input className="input" placeholder="Database name" aria-label="Database"
               value={form.database} onChange={set("database")} />
        <input className="input" placeholder="schema (opt.)" aria-label="Default schema"
               value={form.schema} onChange={set("schema")} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button type="button" className="btn-solid" disabled={!ready || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save credentials"}
        </button>
        <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
          Encrypted at rest. Use a read-only role, ideally on a replica.
        </span>
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

export function MongoCredentialsForm({ slug, onDone }: { slug: string; onDone: () => void }) {
  const [uri, setUri] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.connectMongo(slug, { credentials: { uri: uri.trim() }, status: "active" });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 560 }}>
      <input className="input" placeholder="mongodb+srv://user:pass@host/db" aria-label="Connection URI"
             value={uri} onChange={(e) => setUri(e.target.value)} autoComplete="off" />
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button type="button" className="btn-solid" disabled={!uri.trim() || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save URI"}
        </button>
        <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
          Encrypted at rest. Prefer a read-only user.
        </span>
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

export function ClarityConnectForm({ slug, onDone }: { slug: string; onDone: () => void }) {
  const [token, setToken] = useState("");
  const [projectId, setProjectId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.connectClarity(slug, token.trim(), projectId.trim());
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder="Clarity Data Export token"
        value={token} onChange={(e) => setToken(e.target.value)} aria-label="Clarity Data Export token" />
      <input className="input" placeholder="Project id (Settings → Overview)"
        value={projectId} onChange={(e) => setProjectId(e.target.value)} aria-label="Clarity project id" />
      <div>
        <button type="button" className="btn-solid"
          disabled={!token.trim() || !projectId.trim() || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save token"}
        </button>
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

export function PosthogConnectForm({ slug, onDone }: { slug: string; onDone: () => void }) {
  const [apiKey, setApiKey] = useState("");
  const [projectId, setProjectId] = useState("");
  const [host, setHost] = useState("https://us.posthog.com");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.connectPosthog(slug, apiKey.trim(), projectId.trim(), host.trim() || undefined);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder="Personal API key (phx_…)"
        value={apiKey} onChange={(e) => setApiKey(e.target.value)} aria-label="PostHog personal API key" />
      <input className="input" placeholder="Project id"
        value={projectId} onChange={(e) => setProjectId(e.target.value)} aria-label="PostHog project id" />
      <input className="input" placeholder="https://us.posthog.com"
        value={host} onChange={(e) => setHost(e.target.value)} aria-label="PostHog host" />
      <div>
        <button type="button" className="btn-solid"
          disabled={!apiKey.trim() || !projectId.trim() || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save key"}
        </button>
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

export function GoogleConnectButton({ slug, kinds }: { slug: string; kinds: string[] }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div>
      <button type="button" className="btn-solid" disabled={busy} onClick={async () => {
        setBusy(true);
        setError(null);
        try {
          const start = await api.connect.start(slug, kinds);
          location.href = start.auth_url;
        } catch (e) {
          setError(e instanceof ApiError ? e.message : String(e));
          setBusy(false);
        }
      }}>
        {busy ? "Redirecting…" : "Connect with Google"}
      </button>
      {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

export function MetaConnectButton({ slug, kinds = ["meta_ads"] }: { slug: string; kinds?: string[] }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div>
      <button type="button" className="btn-solid" disabled={busy} onClick={async () => {
        setBusy(true);
        setError(null);
        try {
          const start = await api.connectMeta.start(slug, kinds);
          location.href = start.auth_url;
        } catch (e) {
          setError(e instanceof ApiError ? e.message : String(e));
          setBusy(false);
        }
      }}>
        {busy ? "Redirecting…" : "Connect with Meta"}
      </button>
      {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

/** Secondary path: grant a per-account service-account email instead of OAuth. */
export function GrantAccessInstead({
  slug,
  kinds,
  onDone,
}: {
  slug: string;
  kinds: Array<"ga4" | "gtm" | "gsc">;
  onDone: (note: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [propertyId, setPropertyId] = useState("");
  const [containerPath, setContainerPath] = useState("");
  const [siteUrl, setSiteUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const wantsGa4 = kinds.includes("ga4");
  const wantsGtm = kinds.includes("gtm");
  const wantsGsc = kinds.includes("gsc");

  const begin = async () => {
    setBusy(true);
    setError(null);
    try {
      const start = await api.connect.saStart(slug, kinds);
      setEmail(start.sa_email);
      setHint(kinds.map((k) => start.instructions[k]?.human).filter(Boolean).join(" "));
      setOpen(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: { product_slug: string; ga4_property_id?: string; gtm_container_path?: string; gsc_site_url?: string } = {
        product_slug: slug,
      };
      if (wantsGa4 && propertyId.trim()) body.ga4_property_id = propertyId.trim();
      if (wantsGtm && containerPath.trim()) body.gtm_container_path = containerPath.trim();
      if (wantsGsc && siteUrl.trim()) body.gsc_site_url = siteUrl.trim();
      const res = await api.connect.saVerify(body);
      if (res.activated?.length) {
        onDone(`${res.activated.join(", ")} connected.`);
        return;
      }
      const props = res.options?.ga4_properties ?? [];
      const cs = res.options?.gtm_containers ?? [];
      const sites = res.options?.gsc_sites ?? [];
      if (props.length === 1) setPropertyId(props[0].id);
      if (cs.length === 1) setContainerPath(cs[0].path);
      if (sites.length === 1) setSiteUrl(sites[0].site_url);
      const ga4Ready = !wantsGa4 || props.length === 1;
      const gtmReady = !wantsGtm || cs.length === 1;
      const gscReady = !wantsGsc || sites.length === 1;
      if (ga4Ready && gtmReady && gscReady && (props.length || cs.length || sites.length)) {
        const again = await api.connect.saVerify({
          product_slug: slug,
          ga4_property_id: wantsGa4 ? props[0]?.id : undefined,
          gtm_container_path: wantsGtm ? cs[0]?.path : undefined,
          gsc_site_url: wantsGsc ? sites[0]?.site_url : undefined,
        });
        if (again.activated?.length) {
          onDone(`${again.activated.join(", ")} connected.`);
          return;
        }
      }
      setHint(
        props.length || cs.length || sites.length
          ? "Pick the property / container / site below, then verify again."
          : `Nothing is visible to ${res.sa_email} yet. Add the email, then verify.`,
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {!open && (
        <button type="button" className="btn-ghost" disabled={busy} onClick={() => void begin()}>
          {busy ? "Preparing…" : "Grant access instead"}
        </button>
      )}
      {open && email && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="eyebrow">Service account</span>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              className="btn-text"
              onClick={() => {
                void navigator.clipboard.writeText(email);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <pre className="num" style={{ margin: 0, padding: "10px 12px", fontSize: 12, overflow: "auto" }}>
            {email}
          </pre>
          {hint && <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>{hint}</div>}
          {wantsGa4 && (
            <input className="input" placeholder="GA4 property id"
              value={propertyId} onChange={(e) => setPropertyId(e.target.value)}
              aria-label="GA4 property id" />
          )}
          {wantsGtm && (
            <input className="input" placeholder="GTM container path (accounts/…/containers/…)"
              value={containerPath} onChange={(e) => setContainerPath(e.target.value)}
              aria-label="GTM container path" />
          )}
          {wantsGsc && (
            <input className="input" placeholder="Search Console site (https://example.com/ or sc-domain:…)"
              value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)}
              aria-label="Search Console site URL" />
          )}
          <div>
            <button type="button" className="btn-solid" disabled={busy} onClick={() => void verify()}>
              {busy ? "Checking…" : "Verify access"}
            </button>
          </div>
        </div>
      )}
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

export function TrackingInstall({ slug }: { slug: string }) {
  const info = useAsync(() => api.trackingKey(slug), [slug]);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<"mint" | "deploy" | null>(null);
  const [snippet, setSnippet] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [key, setKey] = useState<string | null>(null);
  const hasKey = Boolean(key ?? info.data?.key);
  // Someone who connected Tag Manager a step ago should not be handed a
  // snippet to paste — the container we hold write access to can take the tag.
  const canDeploy = Boolean(info.data?.gtm_connected);

  const mint = async () => {
    setBusy("mint");
    setNote(null);
    try {
      await api.rotateTrackingKey(slug);
      const next = await api.trackingKey(slug);
      setSnippet(next.snippet);
      setKey(next.key);
      setNote(next.key ? "Key created. Paste the snippet, then wait for the first event." : "No key.");
      setWaiting(true);
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const deploy = async () => {
    if (!window.confirm(GTM_DEPLOY_CONFIRM)) return;
    setBusy("deploy");
    setNote(null);
    try {
      setNote(await deployGtmTag(slug, hasKey));
      setWaiting(true);
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <span className="sub">
        First-party events (leads, signups, payments) land here without a replica.
        Skip if you are connecting a database or GA4 instead.
      </span>
      {snippet && (
        <pre className="secret" style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>{snippet}</pre>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {canDeploy && (
          <button type="button" className="btn-solid" disabled={busy !== null} onClick={() => void deploy()}>
            {busy === "deploy" ? "Publishing…" : "Deploy tag via Tag Manager"}
          </button>
        )}
        <button
          type="button"
          className={canDeploy ? "btn-ghost" : "btn-solid"}
          disabled={busy !== null}
          onClick={() => void mint()}
        >
          {busy === "mint" ? "Working…" : hasKey ? "Rotate key" : "Create tracking key"}
        </button>
      </div>
      {waiting && <span className="sub">Waiting for the first event — you can continue without it.</span>}
      {note && <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>{note}</div>}
    </div>
  );
}

type PickOption = { id: string; name: string };

function TokenPickForm({
  tokenLabel, tokenPlaceholder, pickLabel, hint, busy, error,
  token, onToken, options, selected, onSelect, onDiscover, onSave, canSave,
}: {
  tokenLabel: string;
  tokenPlaceholder: string;
  pickLabel?: string;
  hint: string;
  busy: boolean;
  error: string | null;
  token: string;
  onToken: (v: string) => void;
  options: PickOption[];
  selected: string;
  onSelect: (v: string) => void;
  onDiscover: () => void;
  onSave: () => void;
  canSave: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder={tokenPlaceholder}
        value={token} onChange={(e) => onToken(e.target.value)} aria-label={tokenLabel} />
      {options.length > 0 && pickLabel && (
        <select className="input" value={selected} onChange={(e) => onSelect(e.target.value)} aria-label={pickLabel}>
          <option value="">Select…</option>
          {options.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.id})</option>)}
        </select>
      )}
      <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>{hint}</div>
      <div style={{ display: "flex", gap: 8 }}>
        {options.length === 0 ? (
          <button type="button" className="btn-solid" disabled={!token.trim() || busy} onClick={onDiscover}>
            {busy ? "Checking…" : "Find accounts"}
          </button>
        ) : (
          <button type="button" className="btn-solid" disabled={!canSave || busy} onClick={onSave}>
            {busy ? "Saving…" : "Connect"}
          </button>
        )}
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

export function MetaAdsConnectForm({ slug, onDone }: { slug: string; onDone: () => void }) {
  const [showManual, setShowManual] = useState(false);
  const [token, setToken] = useState("");
  const [accounts, setAccounts] = useState<PickOption[]>([]);
  const [accountId, setAccountId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (id?: string) => {
    setBusy(true); setError(null);
    try {
      const res = await api.connectMetaAds(slug, token.trim(), id);
      if (res.connection_id) { onDone(); return; }
      setAccounts((res.accounts ?? []).map((a) => ({ id: a.id, name: a.name })));
      setBusy(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <MetaConnectButton slug={slug} kinds={["meta_ads"]} />
      </div>
      <div>
        <button
          type="button"
          className="btn-ghost"
          style={{ fontSize: 12, padding: "4px 8px" }}
          onClick={() => setShowManual((v) => !v)}
        >
          {showManual ? "Hide manual token paste" : "Or paste a System User / Access Token"}
        </button>
      </div>
      {showManual && (
        <TokenPickForm tokenLabel="Meta access token" tokenPlaceholder="Meta access token (ads_read)"
          pickLabel="Ad account" hint="A user or system-user token with ads_read. Adult-adjacent projects are refused."
          busy={busy} error={error} token={token} onToken={setToken} options={accounts}
          selected={accountId} onSelect={setAccountId} onDiscover={() => void run()}
          onSave={() => void run(accountId)} canSave={!!accountId} />
      )}
    </div>
  );
}

export function LinkedinAdsConnectForm({ slug, onDone }: { slug: string; onDone: () => void }) {
  const [token, setToken] = useState("");
  const [accounts, setAccounts] = useState<PickOption[]>([]);
  const [accountId, setAccountId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (id?: string) => {
    setBusy(true); setError(null);
    try {
      const res = await api.connectLinkedinAds(slug, token.trim(), id);
      if (res.connection_id) { onDone(); return; }
      setAccounts((res.accounts ?? []).map((a) => ({ id: a.id, name: a.name })));
      setBusy(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };
  return (
    <TokenPickForm tokenLabel="LinkedIn access token" tokenPlaceholder="LinkedIn token (r_ads, r_ads_reporting)"
      pickLabel="Ad account" hint="A Marketing token with r_ads and r_ads_reporting."
      busy={busy} error={error} token={token} onToken={setToken} options={accounts}
      selected={accountId} onSelect={setAccountId} onDiscover={() => void run()}
      onSave={() => void run(accountId)} canSave={!!accountId} />
  );
}

export function MetaSocialConnectForm({ slug, onDone }: { slug: string; onDone: () => void }) {
  const [showManual, setShowManual] = useState(false);
  const [token, setToken] = useState("");
  const [pages, setPages] = useState<PickOption[]>([]);
  const [pageId, setPageId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (id?: string) => {
    setBusy(true); setError(null);
    try {
      const res = await api.connectMetaSocial(slug, token.trim(), id);
      if (res.connection_id) { onDone(); return; }
      setPages((res.pages ?? []).map((p) => ({
        id: p.id,
        name: p.instagram_name ? `${p.name} + @${p.instagram_name}` : p.name,
      })));
      setBusy(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <MetaConnectButton slug={slug} kinds={["meta_social"]} />
      </div>
      <div>
        <button
          type="button"
          className="btn-ghost"
          style={{ fontSize: 12, padding: "4px 8px" }}
          onClick={() => setShowManual((v) => !v)}
        >
          {showManual ? "Hide manual token paste" : "Or paste an Access Token"}
        </button>
      </div>
      {showManual && (
        <TokenPickForm tokenLabel="Meta user token" tokenPlaceholder="Meta user token (pages_show_list)"
          pickLabel="Facebook Page" hint="A user token that can list Pages. Linked Instagram is pulled automatically."
          busy={busy} error={error} token={token} onToken={setToken} options={pages}
          selected={pageId} onSelect={setPageId} onDiscover={() => void run()}
          onSave={() => void run(pageId)} canSave={!!pageId} />
      )}
    </div>
  );
}

export function XSocialConnectForm({ slug, onDone }: { slug: string; onDone: () => void }) {
  const [token, setToken] = useState("");
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.connectXSocial(slug, token.trim(), username.trim());
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder="X API v2 Bearer token"
        value={token} onChange={(e) => setToken(e.target.value)} aria-label="X Bearer token" />
      <input className="input" placeholder="Username (without @)"
        value={username} onChange={(e) => setUsername(e.target.value)} aria-label="X username" />
      <div>
        <button type="button" className="btn-solid"
          disabled={!token.trim() || !username.trim() || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Connect"}
        </button>
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

export function StripeCredentialsForm({ slug, onDone }: { slug: string; onDone: () => void }) {
  const [apiKey, setApiKey] = useState("");
  const [stage, setStage] = useState<"converted" | "payment">("converted");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.connectStripe(slug, apiKey.trim(), stage);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder="Restricted or secret key (rk_live_… / sk_live_…)"
        value={apiKey} onChange={(e) => setApiKey(e.target.value)} aria-label="Stripe API key" />
      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--text-3)" }}>
        Funnel stage for a successful charge
        <select className="input" value={stage} onChange={(e) => setStage(e.target.value as "converted" | "payment")}
          aria-label="Default funnel stage">
          <option value="converted">converted — the money event</option>
          <option value="payment">payment — later stage if converted is already mapped</option>
        </select>
      </label>
      <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>
        Prefer a Restricted key with Charges, Customers, Refunds and Disputes read.
        Encrypted at rest. Successful charges become conversions + revenue; refunds net out.
      </div>
      <div>
        <button type="button" className="btn-solid"
          disabled={!apiKey.trim() || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save key"}
        </button>
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

export function BingConnectForm({ slug, defaultUrl = "", onDone }: { slug: string; defaultUrl?: string; onDone: () => void }) {
  const [apiKey, setApiKey] = useState("");
  const [siteUrl, setSiteUrl] = useState(defaultUrl);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.connectBing(slug, apiKey.trim(), siteUrl.trim());
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder="Bing Webmaster API key"
        value={apiKey} onChange={(e) => setApiKey(e.target.value)} aria-label="Bing Webmaster API key" />
      <input className="input" placeholder="Site URL (https://example.com)"
        value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} aria-label="Bing Webmaster site URL" />
      <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>
        Generate the key in Bing Webmaster → Settings → API access. Use the site URL as it appears in the portal.
      </div>
      <div>
        <button type="button" className="btn-solid"
          disabled={!apiKey.trim() || !siteUrl.trim() || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save key"}
        </button>
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

export function OpinlyConnectForm({ slug, onDone }: { slug: string; onDone: () => void }) {
  const [apiKey, setApiKey] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.connectOpinly(slug, apiKey.trim(), companyId.trim() || undefined);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder="Opinly API key (sk-…)"
        value={apiKey} onChange={(e) => setApiKey(e.target.value)} aria-label="Opinly API key" />
      <input className="input" placeholder="Company id (comp_…, optional if single workspace)"
        value={companyId} onChange={(e) => setCompanyId(e.target.value)} aria-label="Opinly company id" />
      <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>
        Create the key in Opinly → Settings → Developers. Daily snapshot tracks AI-search visibility and keyword ranks.
      </div>
      <div>
        <button type="button" className="btn-solid"
          disabled={!apiKey.trim() || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save key"}
        </button>
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

export function AhrefsConnectForm({ slug, defaultDomain = "", onDone }: { slug: string; defaultDomain?: string; onDone: () => void }) {
  const [apiKey, setApiKey] = useState("");
  const [domain, setDomain] = useState(defaultDomain);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.connectAhrefs(slug, apiKey.trim(), domain.trim());
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder="Ahrefs API v3 token"
        value={apiKey} onChange={(e) => setApiKey(e.target.value)} aria-label="Ahrefs API token" />
      <input className="input" placeholder="Domain to track (example.com)"
        value={domain} onChange={(e) => setDomain(e.target.value)} aria-label="Ahrefs target domain" />
      <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>
        Create a v3 token in Ahrefs → Account → API. Snapshots are taken weekly to conserve API units.
      </div>
      <div>
        <button type="button" className="btn-solid"
          disabled={!apiKey.trim() || !domain.trim() || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save token"}
        </button>
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

export function SemrushConnectForm({ slug, defaultDomain = "", onDone }: { slug: string; defaultDomain?: string; onDone: () => void }) {
  const [apiKey, setApiKey] = useState("");
  const [domain, setDomain] = useState(defaultDomain);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.setSemrushKey(slug, apiKey.trim(), domain.trim());
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder="SEMrush API key"
        value={apiKey} onChange={(e) => setApiKey(e.target.value)} aria-label="SEMrush API key" />
      <input className="input" placeholder="Domain to track (example.com)"
        value={domain} onChange={(e) => setDomain(e.target.value)} aria-label="SEMrush target domain" />
      <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>
        Create the key in SEMrush → Subscription Info → API Units. Snapshots are taken weekly.
      </div>
      <div>
        <button type="button" className="btn-solid"
          disabled={!apiKey.trim() || !domain.trim() || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save key"}
        </button>
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}
